import React, { useState } from "react";
import {
  Box,
  ButtonBase,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import liff from "../../../lib/liff";

import ButtonDialog from "default/components/ButtonDialog";
import Loading from "default/components/Loading";
import { COLORS } from "constants/colors";
import { getMemoryRollbackCandidates, rollbackMemory } from "utils/api";
import AppLayout from "works/chi/components/AppLayout";
import StyledButton from "works/chi/components/StyledButton";

const REASON_MEMORY_LIMIT = "memory_limit";
const REASON_FREE_TEXT = "free_text";

function MemoryRollback() {
  const { t, i18n } = useTranslation(["chi"]);
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const soulId = queryParams.get("soul_id");
  const lng = queryParams.get("lng") || i18n.language || "ja";

  const [step, setStep] = useState(1);
  const [reasonType, setReasonType] = useState(REASON_MEMORY_LIMIT);
  const [freeReason, setFreeReason] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessageKey, setErrorMessageKey] = useState("chi:memoryRollback.error");
  const [openConfirm, setOpenConfirm] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const displayZone = lng === "en" ? "Europe/Warsaw" : "Asia/Tokyo";

  const formatDateTime = (isoString) => {
    const dt = DateTime.fromISO(isoString, { zone: "utc" })
      .setZone(displayZone)
      .setLocale(lng);
    return lng === "en"
      ? dt.toFormat("MMM d, yyyy HH:mm")
      : dt.toFormat("yyyy/MM/dd HH:mm");
  };

  const withLng = (path) => {
    const hasQuery = path.includes("?");
    return lng ? `${path}${hasQuery ? "&" : "?"}lng=${lng}` : path;
  };

  const reasonText =
    reasonType === REASON_MEMORY_LIMIT
      ? t("chi:memoryRollback.reasonMemoryLimit")
      : freeReason.trim();

  const canGoNextFromReason =
    reasonType === REASON_MEMORY_LIMIT || freeReason.trim().length > 0;

  const fetchCandidates = async () => {
    if (!soulId || !targetDate) {
      setErrorMessageKey("chi:memoryRollback.error");
      setIsError(true);
      return;
    }
    try {
      setIsError(false);
      setErrorMessageKey("chi:memoryRollback.error");
      setIsLoadingCandidates(true);
      const data = await getMemoryRollbackCandidates(soulId, {
        date: targetDate,
        timezone: displayZone,
      });
      setCandidates(data.candidates || []);
      setSelectedCandidate(null);
      setStep(3);
    } catch (error) {
      setErrorMessageKey("chi:memoryRollback.error");
      setIsError(true);
    } finally {
      setIsLoadingCandidates(false);
    }
  };

  const handleCandidateClick = (candidate) => {
    setSelectedCandidate(candidate);
    setOpenConfirm(true);
  };

  const handleConfirm = async () => {
    if (!selectedCandidate) {
      return;
    }
    try {
      setIsProcessing(true);
      await rollbackMemory(soulId, selectedCandidate.candidate_id, {
        reasonType,
        reasonText,
      });
      setIsCompleted(true);
      setOpenConfirm(false);
    } catch (error) {
      setErrorMessageKey("chi:memoryRollback.executeError");
      setIsError(true);
      setOpenConfirm(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackToMyPage = () => {
    navigate(withLng(`/chi/mypage?soul_id=${soulId}`));
  };

  const handleBackToChat = () => {
    liff.closeWindow();
  };

  const renderStepTitle = () => {
    if (isCompleted) {
      return t("chi:memoryRollback.completedTitle");
    }
    if (step === 1) {
      return t("chi:memoryRollback.reasonTitle");
    }
    if (step === 2) {
      return t("chi:memoryRollback.dateTitle");
    }
    return t("chi:memoryRollback.candidateTitle");
  };

  const renderReasonStep = () => (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography
        sx={{
          color: COLORS.TEXT_WHITE,
          fontSize: 14,
          lineHeight: 1.8,
          textAlign: "left",
          whiteSpace: "pre-line",
          opacity: 0.9,
        }}
      >
        {t("chi:memoryRollback.reasonNotice")}
      </Typography>
      <FormControl>
        <RadioGroup
          value={reasonType}
          onChange={(event) => setReasonType(event.target.value)}
        >
          <FormControlLabel
            value={REASON_MEMORY_LIMIT}
            control={<Radio sx={{ color: COLORS.TEXT_WHITE }} />}
            label={t("chi:memoryRollback.reasonMemoryLimit")}
            sx={{
              color: COLORS.TEXT_WHITE,
              alignItems: "flex-start",
              mb: 1,
              "& .MuiFormControlLabel-label": {
                fontSize: 14,
                lineHeight: 1.7,
              },
            }}
          />
          <FormControlLabel
            value={REASON_FREE_TEXT}
            control={<Radio sx={{ color: COLORS.TEXT_WHITE }} />}
            label={t("chi:memoryRollback.reasonFreeText")}
            sx={{
              color: COLORS.TEXT_WHITE,
              "& .MuiFormControlLabel-label": { fontSize: 14 },
            }}
          />
        </RadioGroup>
      </FormControl>
      {reasonType === REASON_FREE_TEXT && (
        <TextField
          value={freeReason}
          onChange={(event) => setFreeReason(event.target.value)}
          multiline
          minRows={4}
          placeholder={t("chi:memoryRollback.reasonPlaceholder")}
          fullWidth
          inputProps={{
            "aria-label": t("chi:memoryRollback.reasonFreeText"),
            maxLength: 500,
          }}
          sx={{
            "& .MuiInputBase-root": {
              color: COLORS.TEXT_WHITE,
              background: "rgba(0,0,0,0.28)",
              borderRadius: "6px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255,255,255,0.55)",
            },
            "& .MuiInputBase-input::placeholder": {
              color: "rgba(255,255,255,0.58)",
              opacity: 1,
            },
          }}
        />
      )}
      <StyledButton
        variant="contained"
        disabled={!canGoNextFromReason}
        onClick={() => setStep(2)}
        sx={{ alignSelf: "center", mt: 1 }}
      >
        {t("chi:memoryRollback.next")}
      </StyledButton>
    </Box>
  );

  const renderCompletedStep = () => (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography
        sx={{
          color: COLORS.TEXT_WHITE,
          fontSize: 15,
          lineHeight: 1.8,
          textAlign: "center",
          whiteSpace: "pre-line",
        }}
      >
        {t("chi:memoryRollback.completedDescription")}
      </Typography>
    </Box>
  );

  const renderDateStep = () => (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      <TextField
        type="date"
        value={targetDate}
        onChange={(event) => setTargetDate(event.target.value)}
        fullWidth
        inputProps={{ "aria-label": t("chi:memoryRollback.dateTitle") }}
        sx={{
          "& .MuiInputBase-root": {
            color: COLORS.TEXT_WHITE,
            background: "rgba(0,0,0,0.28)",
            borderRadius: "6px",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255,255,255,0.55)",
          },
          "& input::-webkit-calendar-picker-indicator": {
            filter: "invert(1)",
          },
        }}
      />
      {isError && (
        <Typography
          sx={{
            color: COLORS.TEXT_WHITE,
            fontSize: 14,
            lineHeight: 1.7,
            textAlign: "center",
            whiteSpace: "pre-line",
          }}
        >
          {t(errorMessageKey)}
        </Typography>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
        <StyledButton
          variant="contained"
          disabled={!targetDate || isLoadingCandidates}
          onClick={fetchCandidates}
        >
          {isLoadingCandidates
            ? t("chi:memoryRollback.loading")
            : t("chi:memoryRollback.next")}
        </StyledButton>
        <StyledButton variant="contained" onClick={() => setStep(1)}>
          {t("chi:memoryRollback.previous")}
        </StyledButton>
      </Box>
    </Box>
  );

  const renderCandidate = (candidate) => {
    const texts = candidate.texts && candidate.texts.length > 0
      ? candidate.texts
      : [candidate.text];
    return (
      <ButtonBase
        key={candidate.candidate_id}
        onClick={() => handleCandidateClick(candidate)}
        sx={{
          width: "100%",
          display: "block",
          textAlign: "left",
          border: "1px solid rgba(255,255,255,0.55)",
          borderRadius: "6px",
          px: 2,
          py: 1.5,
          background: "rgba(0,0,0,0.28)",
          color: COLORS.TEXT_WHITE,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            lineHeight: 1.4,
            opacity: 0.82,
            mb: 1,
          }}
        >
          {formatDateTime(candidate.created_at)}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {texts.map((text, index) => (
            <Box
              key={`${candidate.candidate_id}-${index}`}
              sx={{
                alignSelf: "flex-start",
                maxWidth: "100%",
                borderRadius: "6px",
                px: 1.5,
                py: 1,
                background: "rgba(255,255,255,0.12)",
              }}
            >
              <Typography
                sx={{
                  fontSize: 15,
                  lineHeight: 1.7,
                  whiteSpace: "pre-line",
                  overflowWrap: "anywhere",
                }}
              >
                {text}
              </Typography>
            </Box>
          ))}
        </Box>
      </ButtonBase>
    );
  };

  const renderCandidateStep = () => (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      {isLoadingCandidates && <Loading />}

      {isError && (
        <Typography
          sx={{
            color: COLORS.TEXT_WHITE,
            fontSize: 14,
            lineHeight: 1.7,
            textAlign: "center",
            whiteSpace: "pre-line",
          }}
        >
          {t(errorMessageKey)}
        </Typography>
      )}

      {!isError && candidates.length === 0 && (
        <Typography
          sx={{
            color: COLORS.TEXT_WHITE,
            fontSize: 14,
            lineHeight: 1.7,
            textAlign: "center",
            whiteSpace: "pre-line",
          }}
        >
          {t("chi:memoryRollback.empty")}
        </Typography>
      )}

      {!isError && candidates.length > 0 && (
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
          {candidates.map(renderCandidate)}
        </Box>
      )}

      <StyledButton
        variant="contained"
        onClick={() => setStep(2)}
        sx={{ alignSelf: "center", mt: 1 }}
      >
        {t("chi:memoryRollback.previous")}
      </StyledButton>
    </Box>
  );

  return (
    <AppLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "100vh",
          py: 4,
          gap: 3,
        }}
      >
        <Typography
          component="h1"
          sx={{
            color: COLORS.TEXT_WHITE,
            fontFamily: '"Noto Serif JP", serif',
            fontSize: 24,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("chi:memoryRollback.title")}
        </Typography>

        <Typography
          component="h2"
          sx={{
            color: COLORS.TEXT_WHITE,
            fontFamily: '"Noto Serif JP", serif',
            fontSize: 17,
            fontWeight: "bold",
            lineHeight: 1.7,
            textAlign: "center",
            whiteSpace: "pre-line",
          }}
        >
          {renderStepTitle()}
        </Typography>

        {isCompleted && renderCompletedStep()}
        {!isCompleted && step === 1 && renderReasonStep()}
        {!isCompleted && step === 2 && renderDateStep()}
        {!isCompleted && step === 3 && renderCandidateStep()}

        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, mt: 1 }}>
          <StyledButton variant="contained" onClick={handleBackToMyPage}>
            {t("chi:memoryRollback.back")}
          </StyledButton>
          <StyledButton variant="contained" onClick={handleBackToChat}>
            {t("chi:memoryRollback.backToChat")}
          </StyledButton>
        </Box>
      </Box>

      <ButtonDialog
        open={openConfirm}
        title={t("chi:memoryRollback.confirmTitle")}
        description={t("chi:memoryRollback.confirmDescription")}
        buttons={[
          {
            label: t("chi:memoryRollback.execute"),
            onClick: handleConfirm,
            disabled: isProcessing,
          },
        ]}
        handleClose={() => setOpenConfirm(false)}
      />
    </AppLayout>
  );
}

export default MemoryRollback;
